'use client'

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { getAmplifyBackend, createAuthLogger, setAuthSessionCookie, getCurrentUserCognitoCredentials } from '@nestingale/auth';
import { getCustIdFromLS, getUserInfo, getUserCartInfo } from '@/utils/useUserInfo';
import { isHeaderNotActiveOnPage, gtmPageOnloadTracking } from '@/utils';
import { CircularProgress } from '@mui/material';
import NextNProgress from 'nextjs-progressbar';
import { useFavorites } from '@/lib/hooks/useFavorites';
import { useLayout } from '../providers/LayoutProvider';
import { useShopify } from "@/hooks/useShopify";
import { CART_TOKEN, GCART_TOKEN } from "@/utils";
import { configureAmplify } from '@/app/providers/AuthProvider';
import { useAuth } from '@nestingale/auth/hooks';

// configureAmplify is now imported from AuthProvider which uses @nestingale/auth v6

function fetchGeneratePreviewStatusList() { };

function localStorageValues(emailAddress: string, userName: string | undefined) {
  const showCart = typeof window !== 'undefined';
  return {
    emailAddress,
    showCart,
    userName
  }
}

interface ClientLayoutProps {
  children: React.ReactNode;
  initialLayoutData?: any;
}

export default function ClientLayout({ children, initialLayoutData }: ClientLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const layoutContext = useLayout();
  //const { resetRooms3DInfo } = GetOnboardinData();
  const { checkoutState, createCheckout } = useShopify();
  const { userEmail, userName } = useAuth();

  const [isLoaded, setLoaded] = useState(false);
  const [loginEl, setLoginEl] = useState(null);
  const [globalData, setGobalData] = useState({ userInfo: null } as any);
  const [userInfo, setUserInfo] = useState({ status: "INITIATED" } as any);

  // useFavorites — fetches favorites reactively when cId becomes available.
  // No explicit dispatch needed; the query auto-fires when customerId is truthy.
  const { loading: favoritesLoading } = useFavorites({ customerId: userInfo?.cId });

  const userInfoRef = useRef<{ [key: string]: string | boolean }>({ loadStatus: false });
  //const previewGenerationList = builderSelector.generatePreview().previewGenerationList;
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const emailAddress = userEmail;
  const env = process.env.NEXT_PUBLIC_APP_ENV;

  // Configure Amplify on mount and path change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      configureAmplify();
    }
  }, [pathname]);

  // Clear cart cookies after order completion
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const windowObj = window as any;
      let checkoutId = windowObj.getCookie?.(CART_TOKEN);
      console.log('--------ClientLayout -------cart token found. id: ', checkoutId);
      if (checkoutId !== null && checkoutId !== 'null') {
        createCheckout({ checkoutId }).then((response: any) => {
          console.log('[ClientLayout] checkout response completedAt:', response?.completedAt);
          if (response?.completedAt != null) {
            windowObj.setCookieWithoutSecure?.(CART_TOKEN, null, 10);
            windowObj.setCookieWithoutSecure?.(GCART_TOKEN, null, 10);
          }
        });
      }
    }
  }, []);

  // // Load previous statuses from localStorage
  // const loadPreviousStatuses = () => {
  //   const savedStatuses = localStorage.getItem(`previewStatuses${getCustIdFromLS()}`);
  //   return savedStatuses ? JSON.parse(savedStatuses) : {};
  // };

  // // Save statuses to localStorage
  // const savePreviousStatuses = (statuses: Record<string, string>) => {
  //   localStorage.setItem(`previewStatuses${getCustIdFromLS()}`, JSON.stringify(statuses));
  // };

  const custID = getCustIdFromLS();

  // Polling for preview generation status
  // useEffect(() => {
  //   dispatch(fetchGeneratePreviewStatusList() as any);
  // }, [custID]);

  // useEffect(() => {
  //   if (!previewGenerationList.length) return;

  //   let previousStatuses = loadPreviousStatuses();
  //   let statusChanged = false;
  //   let updatedStatuses: Record<string, string> = {};

  //   const isPollingNeeded = previewGenerationList.some(
  //     (project: any) =>
  //       project.previewStatus.toLowerCase() === "initiated" ||
  //       project.previewStatus.toLowerCase() === "processing"
  //   );

  //   previewGenerationList.forEach((project: any) => {
  //     const prevStatus = previousStatuses[project.projectId] || null;
  //     updatedStatuses[project.projectId] = project.previewStatus;

  //     if (
  //       (prevStatus?.toLowerCase() === "initiated" || prevStatus?.toLowerCase() === "processing") &&
  //       (project.previewStatus.toLowerCase() === "completed" || project.previewStatus.toLowerCase() === "failed")
  //     ) {
  //       statusChanged = true;
  //       //dispatch(actions.showGeneratePreviewSettledIndicator(true));
  //     }
  //   });

  //   savePreviousStatuses(updatedStatuses);

  //   if (isPollingNeeded) {
  //     if (!pollingIntervalRef.current) {
  //       pollingIntervalRef.current = setInterval(() => {
  //         dispatch(fetchGeneratePreviewStatusList() as any);
  //       }, 5 * 60 * 1000);
  //     }
  //   } else {
  //     if (pollingIntervalRef.current) {
  //       clearInterval(pollingIntervalRef.current);
  //       pollingIntervalRef.current = null;
  //     }
  //   }

  //   return () => {
  //     if (!isPollingNeeded && pollingIntervalRef.current) {
  //       clearInterval(pollingIntervalRef.current);
  //       pollingIntervalRef.current = null;
  //     }
  //   };
  // }, [previewGenerationList]);

  const localStorageObj = useMemo(() => {
    return localStorageValues(emailAddress as string, userName as string);
  }, [emailAddress, userName]);

  // Reset rooms 3D info on route change
  // useEffect(() => {
  //   resetRooms3DInfo();
  // }, [pathname]);

  // Get user info on mount
  useEffect(() => {
    if (isHeaderNotActiveOnPage()) {
      return;
    }
    //@ts-ignore
    window.checkingForUserSession = true;
    getUserInfo("", true).then((userInfo) => {
      console.log("--- ClientLayout : getUserInfo", JSON.stringify(userInfo));
      setUserInfo(userInfo ? { ...userInfo, status: "DONE" } : { status: "DONE" });
    });
  }, []);

  // Update global data when user info changes
  useEffect(() => {
    console.log("--- ClientLayout : userInfo", JSON.stringify(userInfo));
    //@ts-ignore
    window.checkingForUserSession = false;
    if (userInfo.status === "DONE") {
      userInfoRef.current.loadStatus = true;
      setGobalData({ userInfo, lsv: localStorageObj });
      if (userInfo.email) {
        userInfoRef.current = { ...userInfoRef.current, ...userInfo, ...localStorageObj };
      } else {
        userInfoRef.current = { ...userInfoRef.current, unauthorized: true };
      }
    }
  }, [userInfo, localStorageObj]);

  // useFavorites handles fetching reactively via React Query (above) — no explicit dispatch needed.

  // Sync local state to LayoutProvider context
  useEffect(() => {
    if (layoutContext.setGlobalData) {
      layoutContext.setGlobalData(globalData);
    }
  }, [globalData]);

  useEffect(() => {
    if (layoutContext.setLoginEl) {
      layoutContext.setLoginEl(loginEl);
    }
  }, [loginEl]);

  // When userInfo resolves with an email (initial mount or login), sync the session cookie.
  // setAuthSessionCookie (from @nestingale/auth) POSTs to /api/auth/session and
  // dispatches the 'nest:auth:login' event so ProductGrid can re-fetch pricing.
  useEffect(() => {
    console.log('[ClientLayout][SessionCookie] useEffect fired — userInfo.status:', userInfo?.status, 'email:', userInfo?.email);
    if (userInfo?.status === 'DONE' && userInfo?.email) {
      getAmplifyBackend().getSession().then((session) => {
        console.log('[ClientLayout][SessionCookie] Resolved session details:', session);
        const loginType = session?.loginType || userInfo.loginType || 'unknown';
        const userType = session?.userType || 'customer';
        const attributes = session?.attributes;

        console.log('[ClientLayout][SessionCookie] Calling setAuthSessionCookie — email:', userInfo.email, 'loginType:', loginType, 'userType:', userType, 'attributes:', attributes);
        setAuthSessionCookie(userInfo.email, loginType, {
          endpoint: '/showroom/api/auth/session',
          userType,
          attributes,
        }).then((ok) => {
          console.log('[ClientLayout][SessionCookie] setAuthSessionCookie result:', ok, '— nest:auth:login event should have been dispatched if ok=true');
        });
      }).catch((err) => {
        console.error('[ClientLayout][SessionCookie] Failed to fetch session inside useEffect:', err);
        const cogCreds = getCurrentUserCognitoCredentials();
        setAuthSessionCookie(userInfo.email, userInfo.loginType, {
          endpoint: '/showroom/api/auth/session',
          userType: cogCreds?.userType || 'customer',
        });
      });
    } else {
      console.log('[ClientLayout][SessionCookie] Skipped — status:', userInfo?.status, 'email:', userInfo?.email);
    }
  }, [userInfo?.status, userInfo?.email]);

  // Login callback
  globalData.loginCallback = (callbackObj: { type: string, status: string, userInfo: any }) => {
    let capType = callbackObj.type.charAt(0).toUpperCase() + callbackObj.type.slice(1);
    if (callbackObj.status === "LOGIN_SUCCESS") {
      console.log("[ClientLayout][loginCallback] LOGIN_SUCCESS received. callbackObj:", JSON.stringify(callbackObj));
      // Step 1: Set session cookie immediately via JWT session (LS may still be empty at this point).
      // This triggers AUTH_LOGIN_EVENT → ProductGrid re-fetches pricing.
      getAmplifyBackend().getSession().then((session) => {
        console.log('[ClientLayout][loginCallback] JWT session email:', session?.email);
        if (session?.email) {
          const cogCreds = getCurrentUserCognitoCredentials();
          const userType = session?.userType || cogCreds?.userType || 'customer';
          const loginType = session?.loginType || 'cognito';
          const attributes = session?.attributes;
          
          console.log('[ClientLayout][loginCallback] Calling setAuthSessionCookie — email:', session.email, 'loginType:', loginType, 'userType:', userType, 'attributes:', attributes);
          setAuthSessionCookie(session.email, loginType, {
            endpoint: '/showroom/api/auth/session',
            userType,
            attributes,
          }).then((ok) => {
            console.log('[ClientLayout][loginCallback] setAuthSessionCookie result:', ok);
          });
          // Step 2: Update broader userInfo state (header display, favorites, etc.)
          // Pass email explicitly so getUserInfo doesn't need it from LS.
          getUserInfo(session.email, true).then((freshUserInfo) => {
            console.log('[ClientLayout][loginCallback] getUserInfo resolved:', JSON.stringify(freshUserInfo));
            setUserInfo(freshUserInfo ? { ...freshUserInfo, status: "DONE" } : { email: session.email, status: "DONE" });
          });
        } else {
          console.warn('[ClientLayout][loginCallback] No email in JWT session — falling back to getUserInfo');
          getUserInfo('', true).then((freshUserInfo) => {
            setUserInfo(freshUserInfo ? { ...freshUserInfo, status: "DONE" } : { status: "DONE" });
          });
        }
      }).catch(() => {
        // JWT unavailable — fall back to LS-based getUserInfo
        getUserInfo('', true).then((freshUserInfo) => {
          setUserInfo(freshUserInfo ? { ...freshUserInfo, status: "DONE" } : { status: "DONE" });
        });
      });
      gtmPageOnloadTracking({ event: "User_Web_Login_Successful_" + capType, status: callbackObj.status });
    } else {
      console.log("[ClientLayout][loginCallback] LOGIN FAILED:", callbackObj.status);
      gtmPageOnloadTracking({ event: "User_Web_Login_Failed_" + capType, status: callbackObj.status });
    }
  };

  return (
    <>
      <NextNProgress />
      {children}
    </>
  );
}
